import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as PreviewIcon,
  ReceiptLong as GenerateIcon,
  Inventory2 as ExportIcon,
} from '@mui/icons-material';

import {
  adminApi,
  AdminExportJob,
  BulkInvoiceMode,
  BulkInvoiceRow,
  BulkInvoiceRowStatus,
  Organization,
} from '../services/api';
import {
  formatPeriodLabel,
  hasBillingDetails,
  monthSpan,
  selectablePeriods,
} from './bulk-invoice-periods';
import {
  BULK_INVOICE_ORG_FILTERS,
  BULK_INVOICE_ORG_FILTER_LABELS,
  BulkInvoiceOrgFilter,
  filterOrganizations,
  hasBillingSignalData,
} from './bulk-invoice-org-filter';
import { formatZar } from '../utils/format';

/**
 * Bulk invoice generation + export (Trello #363).
 *
 * Pick a month range and either specific organisations or all of them, then:
 *  - Preview  — see what each org WOULD be invoiced. Commits nothing.
 *  - Generate — build the invoices and download them all as one ZIP.
 *  - Export   — download invoices that already exist, without generating.
 *
 * Preview leads deliberately: a back-dated invoice is built from the org's
 * CURRENT billing details and seats, so the amounts are worth eyeballing before
 * anything is committed.
 *
 * The "Show" filter (Marcus, 2026-08-03) replaces the original "Only
 * organisations with billing details" checkbox: an admin here is choosing WHO to
 * invoice, so the axis that matters is how the org pays — Paying (the default)
 * and its three channels, Subscribing / EFT / Managed. See
 * `bulk-invoice-org-filter.ts` for the predicates.
 */

const STATUS_STYLE: Record<BulkInvoiceRowStatus, { label: string; color: 'success' | 'info' | 'warning' | 'error' | 'default' }> = {
  ready: { label: 'Ready', color: 'success' },
  generated: { label: 'Generated', color: 'success' },
  exported: { label: 'Exported', color: 'success' },
  missing: { label: 'No invoice', color: 'warning' },
  skipped: { label: 'Skipped', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
  'not-attempted': { label: 'Not attempted', color: 'default' },
};

const MODE_LABEL: Record<BulkInvoiceMode, string> = {
  preview: 'Preview',
  generate: 'Generate',
  export: 'Export existing',
};

/**
 * Only open a URL the backend handed us if it is actually an https one — a
 * misconfigured or compromised backend returning a `javascript:`/`data:` URL
 * must not be handed straight to window.open. 'noopener' because window.open,
 * unlike a target=_blank anchor, does not imply it.
 */
function openDownload(url: string | undefined): void {
  if (!url || !/^https:\/\//i.test(url)) return;
  window.open(url, '_blank', 'noopener');
}

const BulkInvoicesPage: React.FC = () => {
  const periods = useMemo(() => selectablePeriods(), []);

  const [fromPeriod, setFromPeriod] = useState(periods[1] || periods[0]!);
  const [toPeriod, setToPeriod] = useState(periods[1] || periods[0]!);
  const [allOrganizations, setAllOrganizations] = useState(false);
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  // Defaults to the orgs we actually invoice — the reason an admin opens this
  // page (Marcus, Trello #363). Falls back to 'all' when no billing signals came
  // back, see `effectiveOrgFilter`.
  const [orgFilter, setOrgFilter] = useState<BulkInvoiceOrgFilter>('paying');
  const [search, setSearch] = useState('');

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [running, setRunning] = useState<BulkInvoiceMode | null>(null);
  const [job, setJob] = useState<AdminExportJob | null>(null);
  const [rows, setRows] = useState<BulkInvoiceRow[] | null>(null);
  const [lastMode, setLastMode] = useState<BulkInvoiceMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await adminApi.listOrganizations();
        if (!cancelled && response.success) {
          setOrganizations(response.data.organizations || []);
        }
      } catch {
        if (!cancelled) setError('Failed to load organizations');
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [stopPolling]);

  // The billing signals (subscriptionTag / eftPayer / billingMode) come from the
  // backend's org-subscriptions join. When NONE of the loaded orgs carry any of
  // them — the sales-led IA build, or a backend deployed behind this frontend —
  // the filter is hidden and every org is shown, rather than a "Paying
  // organisations" default silently narrowing the list to nothing.
  const billingSignalsAvailable = useMemo(
    () => hasBillingSignalData(organizations),
    [organizations],
  );
  const effectiveOrgFilter: BulkInvoiceOrgFilter = billingSignalsAvailable ? orgFilter : 'all';

  /** Every org matching the filter, ignoring the free-text search. */
  const filteredOrgs = useMemo(
    () => filterOrganizations(organizations, effectiveOrgFilter),
    [organizations, effectiveOrgFilter],
  );

  const visibleOrgs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return filteredOrgs
      .filter((o) =>
        term ? `${o.displayName || ''} ${o.name || ''}`.toLowerCase().includes(term) : true,
      )
      .sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name));
  }, [filteredOrgs, search]);

  // A tick made under one filter must not silently invoice an org the admin can
  // no longer see, so the selection is INTERSECTED with the filter rather than
  // pruned on change (switching back restores the earlier ticks).
  const selectedFilteredIds = useMemo(() => {
    const inFilter = new Set(filteredOrgs.map((o) => o.id));
    return selectedOrgIds.filter((id) => inFilter.has(id));
  }, [filteredOrgs, selectedOrgIds]);

  const monthCount = useMemo(() => monthSpan(fromPeriod, toPeriod), [fromPeriod, toPeriod]);

  const orgCount = allOrganizations ? filteredOrgs.length : selectedFilteredIds.length;
  const totalUnits = orgCount * monthCount;
  const rangeInvalid = monthCount === 0;
  const nothingSelected = orgCount === 0;

  // "All organisations" must mean all of the ones the filter is showing —
  // otherwise ticking it under "Paying organisations" would invoice every org on
  // the stack. Only the unfiltered case delegates the org-set to the backend.
  const allSelectionLabel =
    effectiveOrgFilter === 'all'
      ? `All organisations${organizations.length ? ` (${organizations.length})` : ''}`
      : `All ${BULK_INVOICE_ORG_FILTER_LABELS[effectiveOrgFilter].toLowerCase()} (${filteredOrgs.length})`;

  const pollJob = useCallback(
    async (jobId: string, mode: BulkInvoiceMode) => {
      try {
        const response = await adminApi.getExportJobStatus(jobId);
        if (!response.success) return;

        const next = response.data as AdminExportJob;
        setJob(next);

        if (next.status === 'COMPLETED') {
          stopPolling();
          setRunning(null);
          setRows(next.metadata?.bulkInvoiceRows || []);
          setSuccess(
            mode === 'preview'
              ? 'Preview ready — nothing has been invoiced yet.'
              : 'Done. Your download should have started.',
          );
          openDownload(next.downloadUrl);
        } else if (next.status === 'FAILED') {
          stopPolling();
          setRunning(null);
          setError(next.errorMessage || 'The run failed');
        }
      } catch (err: any) {
        stopPolling();
        setRunning(null);
        setError(err.response?.data?.message || err.message || 'Failed to check the run status');
      }
    },
    [stopPolling],
  );

  const start = async (mode: BulkInvoiceMode) => {
    setError(null);
    setSuccess(null);
    setRows(null);
    setJob(null);
    setRunning(mode);
    setLastMode(mode);

    try {
      const response = await adminApi.bulkGenerateInvoices({
        mode,
        fromPeriod,
        toPeriod,
        // `allOrganizations` lets the backend resolve the caller's whole stack —
        // correct ONLY when no filter is narrowing the set. With a filter on, the
        // matching ids are sent explicitly so "All paying organisations" doesn't
        // quietly become "every organisation".
        ...(allOrganizations && effectiveOrgFilter === 'all'
          ? { allOrganizations: true }
          : {
              organizationIds: allOrganizations
                ? filteredOrgs.map((o) => o.id)
                : selectedFilteredIds,
            }),
      });

      if (response.success && response.data?.jobId) {
        const { jobId } = response.data;
        setJob({ jobId, status: 'QUEUED', progress: 0, currentStep: 'Queued...' });
        pollingRef.current = setInterval(() => pollJob(jobId, mode), 2000);
        pollJob(jobId, mode);
      } else {
        setRunning(null);
        setError('The run could not be started');
      }
    } catch (err: any) {
      setRunning(null);
      setError(err.response?.data?.message || err.message || 'The run could not be started');
    }
  };

  const toggleOrg = (orgId: string) => {
    setSelectedOrgIds((prev) =>
      prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId],
    );
  };

  const totalInvoiced = useMemo(
    () => (rows || []).reduce((sum, r) => sum + (typeof r.amountZar === 'number' ? r.amountZar : 0), 0),
    [rows],
  );

  const disabled = running !== null || rangeInvalid || nothingSelected || totalUnits === 0;

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Bulk Invoices
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Generate and download invoices for many organisations and months at once. Start with a
        preview — it shows what each organisation would be invoiced without creating anything.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            1. Choose the months
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="from-period-label">From</InputLabel>
                <Select
                  labelId="from-period-label"
                  label="From"
                  value={fromPeriod}
                  onChange={(e) => setFromPeriod(e.target.value)}
                >
                  {periods.map((p) => (
                    <MenuItem key={p} value={p}>
                      {formatPeriodLabel(p)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="to-period-label">To</InputLabel>
                <Select
                  labelId="to-period-label"
                  label="To"
                  value={toPeriod}
                  onChange={(e) => setToPeriod(e.target.value)}
                >
                  {periods.map((p) => (
                    <MenuItem key={p} value={p}>
                      {formatPeriodLabel(p)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {rangeInvalid && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              The “From” month must be the same as or earlier than the “To” month.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            2. Choose the organisations
          </Typography>

          {billingSignalsAvailable && (
            <FormControl size="small" sx={{ minWidth: 260, mb: 1, display: 'block' }}>
              <InputLabel id="org-filter-label">Show</InputLabel>
              <Select
                labelId="org-filter-label"
                label="Show"
                value={orgFilter}
                sx={{ minWidth: 260 }}
                onChange={(e) => setOrgFilter(e.target.value as BulkInvoiceOrgFilter)}
              >
                {BULK_INVOICE_ORG_FILTERS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {BULK_INVOICE_ORG_FILTER_LABELS[f]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            sx={{ display: 'flex' }}
            control={
              <Checkbox
                checked={allOrganizations}
                onChange={(e) => setAllOrganizations(e.target.checked)}
              />
            }
            label={allSelectionLabel}
          />

          {!allOrganizations && (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1, mb: 1 }}>
                <TextField
                  size="small"
                  label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 240 }}
                />
                <Button size="small" onClick={() => setSelectedOrgIds(visibleOrgs.map((o) => o.id))}>
                  Select all shown
                </Button>
                <Button size="small" onClick={() => setSelectedOrgIds([])}>
                  Clear
                </Button>
              </Stack>

              {orgsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Box sx={{ maxHeight: 280, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  {visibleOrgs.length === 0 ? (
                    <Typography color="text.secondary" sx={{ p: 1 }}>
                      No organisations match.
                    </Typography>
                  ) : (
                    visibleOrgs.map((org) => (
                      <FormControlLabel
                        key={org.id}
                        sx={{ display: 'flex' }}
                        control={
                          <Checkbox
                            checked={selectedOrgIds.includes(org.id)}
                            onChange={() => toggleOrg(org.id)}
                          />
                        }
                        label={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <span>{org.displayName || org.name}</span>
                            {!hasBillingDetails(org) && (
                              <Chip size="small" color="warning" label="No billing details" />
                            )}
                          </Stack>
                        }
                      />
                    ))
                  )}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            3. Run it
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {nothingSelected
              ? allOrganizations
                ? 'No organisations match this filter — widen it under “Show”.'
                : 'Select at least one organisation, or tick the box above to take them all.'
              : `${orgCount} organisation${orgCount === 1 ? '' : 's'} × ${monthCount} month${monthCount === 1 ? '' : 's'} = ${totalUnits} invoice${totalUnits === 1 ? '' : 's'}.`}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              startIcon={running === 'preview' ? <CircularProgress size={18} color="inherit" /> : <PreviewIcon />}
              disabled={disabled}
              onClick={() => start('preview')}
            >
              Preview
            </Button>
            <Button
              variant="outlined"
              startIcon={running === 'generate' ? <CircularProgress size={18} color="inherit" /> : <GenerateIcon />}
              disabled={disabled}
              onClick={() => start('generate')}
            >
              Generate &amp; download
            </Button>
            <Button
              variant="outlined"
              startIcon={running === 'export' ? <CircularProgress size={18} color="inherit" /> : <ExportIcon />}
              disabled={disabled}
              onClick={() => start('export')}
            >
              Export existing
            </Button>
          </Stack>

          {job && running && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {job.currentStep || 'Working...'}
              </Typography>
              <LinearProgress variant="determinate" value={job.progress || 0} />
            </Box>
          )}
        </CardContent>
      </Card>

      {rows && (
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">
                {lastMode ? MODE_LABEL[lastMode] : 'Result'} — {rows.length} row{rows.length === 1 ? '' : 's'}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography color="text.secondary">
                  Total: <strong>{formatZar(totalInvoiced)}</strong>
                </Typography>
                {job?.downloadUrl && (
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => openDownload(job.downloadUrl)}
                  >
                    Download again
                  </Button>
                )}
              </Stack>
            </Stack>

            {job?.metadata?.truncated && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The run reached its time limit before finishing every month. Re-run for the
                remaining months — invoices that were already created keep their numbers.
              </Alert>
            )}

            {job?.metadata?.bulkInvoiceRowsOmitted && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This run was too large to list here. The downloaded file has every
                organisation and month.
              </Alert>
            )}

            <Divider sx={{ mb: 2 }} />

            <TableContainer sx={{ maxHeight: 480, overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Organisation</TableCell>
                    <TableCell>Month</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invoice</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={`${row.organizationId}-${row.period}`}>
                      <TableCell>{row.organizationName}</TableCell>
                      <TableCell>{formatPeriodLabel(row.period)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_STYLE[row.status].label}
                          color={STATUS_STYLE[row.status].color}
                        />
                      </TableCell>
                      <TableCell>{row.invoiceNumber || '—'}</TableCell>
                      <TableCell align="right">
                        {typeof row.amountZar === 'number' ? formatZar(row.amountZar) : '—'}
                      </TableCell>
                      <TableCell>{row.note || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BulkInvoicesPage;
